import { computed, inject, Injectable, signal } from '@angular/core';
import { CollectionReturnValue, Core, NodeCollection, NodeSingular } from 'cytoscape';

import {
    EDGE_DEFAULT_Z_INDEX,
    NODE_DEFAULT_Z_INDEX,
} from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.constants';
import { NodeType } from '@connect-services/client/components/synoptic/synoptic-graph/graph-core/synoptic-graph.model';
import { SelectedNode } from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { EntityProperties } from '@connect-services/client/models/panel.model';
import { PlanTypeEnum } from '@connect-services/client/sdk/api';
import { SynopticPlanLayoutService } from '@connect-services/client/services/synoptic/synoptic-plan-layout.service';

const NETWORK_PATH_HIDDEN_CLASS = 'network-path-hidden';

@Injectable({
    providedIn: 'root',
})
export class SynopticGraphService {
    public cy!: Core;
    public readonly focusedNode = signal<SelectedNode | null>(null);
    public readonly planType = computed<PlanTypeEnum | null>(() => {
        const focusedNode = this.focusedNode();
        if (!focusedNode) {
            return null;
        }

        const typeCode: string = focusedNode.dataNode.typeCode;
        switch (typeCode) {
            case NodeType.BPE:
            case NodeType.BPI:
            case NodeType.PB:
            case NodeType.PBO:
                return PlanTypeEnum.BOX;
            case NodeType.FERME:
            case NodeType.BAIE:
                return PlanTypeEnum.BAY;
            case NodeType.EDGE:
            default:
                return null;
        }
    });

    private readonly synopticPlanLayoutService = inject(SynopticPlanLayoutService);

    public filterByNetworkPath(nodeUuids: ReadonlySet<string>, edgeUuids: ReadonlySet<string>): void {
        if (!this.cy) {
            return;
        }

        if (!nodeUuids.size || !edgeUuids.size) {
            this.resetNetworkPathFilter(true);

            return;
        }

        this.cy.batch(() => {
            this.resetNetworkPathFilter();

            this.cy.nodes().forEach((node) => {
                const uuid = node.data('uuid');

                if (!uuid || !nodeUuids.has(uuid)) {
                    node.addClass(NETWORK_PATH_HIDDEN_CLASS);
                    node.style('display', 'none');
                }
            });

            this.cy.edges().forEach((edge) => {
                const uuid = edge.data('uuid');

                if (!uuid || !edgeUuids.has(uuid)) {
                    edge.addClass(NETWORK_PATH_HIDDEN_CLASS);
                    edge.style('display', 'none');
                }
            });
        });

        this.fitVisibleElements();
    }

    public async focusNode(dataEntity: EntityProperties): Promise<void> {
        const targetNode: CollectionReturnValue = this.cy.filter((node) => node.data('uuid') === dataEntity.uuid);

        if (!targetNode.nonempty() || !targetNode.visible()) {
            return;
        }

        await new Promise((resolve) => {
            this.cy.ready(() => {
                const node = this.synopticPlanLayoutService.prepareNode(targetNode);

                if (node.dataNode.uuid === this.focusedNode()?.dataNode.uuid) {
                    return;
                }

                this.focusedNode.set(node);
            });
            resolve(true);
        });
    }

    public resetNetworkPathFilter(fitView: boolean = false): void {
        if (!this.cy) {
            return;
        }

        const hiddenElements = this.cy.elements(`.${NETWORK_PATH_HIDDEN_CLASS}`);
        const hasNetworkPathFilter = hiddenElements.nonempty();

        hiddenElements.removeStyle('display');
        hiddenElements.removeClass(NETWORK_PATH_HIDDEN_CLASS);

        if (fitView && hasNetworkPathFilter) {
            this.fitVisibleElements();
        }
    }

    public setCytoscapeInstance(cytoscape: Core): void {
        this.cy = cytoscape;
    }

    public setToTop(node: NodeSingular, cy: Core): void {
        cy.elements('.selected').each((e) => {
            const zIndex = e.isEdge() ? EDGE_DEFAULT_Z_INDEX : NODE_DEFAULT_Z_INDEX;
            e.removeClass('selected').style('z-index', zIndex).style('z-compound-depth', 'auto');
        });

        let target: NodeCollection;
        if (node.isEdge()) {
            target = node.connectedNodes();
        } else {
            target = node;
        }
        if (target.predecessors) {
            target.predecessors().addClass('selected').style('z-index', 5);
        }
        if (target.successors) {
            target.successors().addClass('selected').style('z-index', 5);
        }

        // Fix first edge overlay
        // Make sure to pull the original clicked node to front
        node.addClass('selected').style('z-compound-depth', 'top').style('z-index', 10);
    }

    public switchVisibility(node: NodeSingular, cy: Core): void {
        this.setToTop(node, cy);
        this.switchVisibilityNodes(node);
    }

    public zoomPlanElement(node: SelectedNode | null): void {
        if (!node) {
            return;
        }

        const targetNode = node.rawNode;

        if (targetNode.hasClass('hide')) {
            targetNode.successors().removeClass('hide');
            targetNode.predecessors().removeClass('hide');
            targetNode.removeClass('hide');
        }

        this.cy.animate(
            { fit: { eles: targetNode[1] ?? targetNode, padding: 20 } },
            {
                duration: 400,
            },
        );
    }

    private fitVisibleElements(): void {
        requestAnimationFrame(() => {
            this.cy.resize();

            const visibleElements = this.cy.elements(':visible');

            if (visibleElements.nonempty()) {
                this.cy.fit(visibleElements, 20);
            }
        });
    }

    private switchVisibilityNodes(parentNode: NodeSingular): void {
        if (parentNode.data('hide-successor')) {
            parentNode.successors().removeClass('hide');
            parentNode.data('hide-successor', false);

            return;
        }
        parentNode.successors().addClass('hide');
        parentNode.removeClass('hide');
        parentNode.data('hide-successor', true);
    }
}
